import { useToast } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { type ColumnMappings, CompanySelectionTypes } from '@revelio/core';

import CompanyMapping from '../../deliverables/company-selection/company-mapping/company-mapping';
import {
  CompanyMappingNameChoice,
  type CompanyMappingStateValue,
} from '../../deliverables/company-selection/company-mapping/company-mapping.model';
import {
  getCompanyMapping,
  resetCompanyMapping,
} from '../../deliverables/company-selection/company-mapping/company-mapping.respository';
import { getSubsidiaryColumnMappings } from '../../deliverables/company-selection/company-mapping/company-mapping.store';
import {
  resetSubsidiaryColumnMappings,
  resetSubsidiarySelectedColumns,
} from '../../deliverables/company-selection/subsidiary-mapping/subsidiary-mapping.repository';
import { createCompanyMapperFromSelections } from '../api/company-mapper-api';
import type { DataBuilderCompanyMapper } from '../api/data-builder-api.types';
import {
  type DataBuilderCompanySelections,
  useDataBuilderActions,
} from '../data-builder.store';

type DataBuilderMapCompaniesFlowProps = {
  instanceId: string;
  isOpen: boolean;
  onRequestClose: () => void;
  onApplySelections: (selections: DataBuilderCompanySelections) => void;
  onApplyMapper: (mapper: DataBuilderCompanyMapper | null) => void;
  onFlowComplete: () => void;
  onSubsidiaryEdit: (mappings: ColumnMappings) => void;
  onDirtyChange: (isDirty: boolean) => void;
};

const toSelections = (
  mappings: CompanyMappingStateValue[],
  nameChoice: CompanyMappingNameChoice
): DataBuilderCompanySelections =>
  mappings.reduce<DataBuilderCompanySelections>((result, mapping) => {
    const selectedName =
      nameChoice === CompanyMappingNameChoice.REVELIO_COMPANY_NAME
        ? mapping.response.primary_name
        : mapping.request.company_name;
    const rcidKey = String(mapping.response.rcid);

    return {
      ...result,
      [rcidKey]: {
        id: rcidKey,
        title: selectedName,
        label: selectedName,
        primary_name: selectedName,
        company_id: mapping.response.company_id,
        rcid: mapping.response.rcid,
        headCount: mapping.response.raw_emp_count,
        hasSubsidiaries: mapping.response.has_subsidiaries,
        isSelected: true,
      },
    };
  }, {});

const clearDeliverablesMapState = () => {
  resetCompanyMapping();
  resetSubsidiaryColumnMappings();
  resetSubsidiarySelectedColumns();
};

export const DataBuilderMapCompaniesFlow = ({
  instanceId,
  isOpen,
  onRequestClose,
  onApplySelections,
  onApplyMapper,
  onFlowComplete,
  onSubsidiaryEdit,
  onDirtyChange,
}: DataBuilderMapCompaniesFlowProps) => {
  const toast = useToast();
  const { startCompanyMapperRequest } = useDataBuilderActions();
  const autoSubmitInFlightRef = useRef(false);
  const [companyColumnSelectionModalOpen, setCompanyColumnSelectionModalOpen] =
    useState<CompanySelectionTypes | null>(
      CompanySelectionTypes.UPLOAD_COMPANIES
    );
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);

  useEffect(() => {
    if (isOpen) {
      clearDeliverablesMapState();
      setCompanyColumnSelectionModalOpen(
        CompanySelectionTypes.UPLOAD_COMPANIES
      );
      setShouldAutoSubmit(false);
      onDirtyChange(true);
      return;
    }

    clearDeliverablesMapState();
    setCompanyColumnSelectionModalOpen(CompanySelectionTypes.UPLOAD_COMPANIES);
    setShouldAutoSubmit(false);
    onDirtyChange(false);
  }, [isOpen, onDirtyChange]);

  useEffect(() => {
    if (!shouldAutoSubmit || !isOpen) {
      autoSubmitInFlightRef.current = false;
      return;
    }

    if (autoSubmitInFlightRef.current) {
      return;
    }

    autoSubmitInFlightRef.current = true;

    const completeSelection = () => {
      const mapping = getCompanyMapping();
      if (mapping === null) {
        toast({
          title: 'Company mapping is unavailable.',
          status: 'error',
        });
        setShouldAutoSubmit(false);
        return;
      }

      const selectedMappings = mapping.verifiedMappings.filter(
        (item) => item.isExcluded !== true
      );
      const selections = toSelections(
        selectedMappings,
        mapping.companyNameChoice ??
          CompanyMappingNameChoice.REVELIO_COMPANY_NAME
      );
      onApplySelections(selections);
      onApplyMapper({
        type: 'company_mapper',
        pipeline_input: '',
        company_reference: '',
        selection_type: 'file',
      });
      onFlowComplete();
      onDirtyChange(false);

      const { complete, fail } = startCompanyMapperRequest(instanceId);
      createCompanyMapperFromSelections(selections, 'file').then(
        (mapper) => {
          onApplyMapper(mapper);
          complete();
        },
        () => {
          onApplyMapper(null);
          toast({
            title: 'Failed to create company reference table',
            status: 'error',
          });
          fail();
        }
      );

      setShouldAutoSubmit(false);
    };

    completeSelection();
  }, [
    instanceId,
    isOpen,
    onApplyMapper,
    onApplySelections,
    onDirtyChange,
    onFlowComplete,
    shouldAutoSubmit,
    startCompanyMapperRequest,
    toast,
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <CompanyMapping
      id="data-builder-company-mapping"
      companyColumnSelectionModalOpen={companyColumnSelectionModalOpen}
      setCompanyColumnSelectionModalOpen={(nextState) => {
        setCompanyColumnSelectionModalOpen((previousState) => {
          const resolvedState =
            typeof nextState === 'function'
              ? nextState(previousState)
              : nextState;

          if (resolvedState === CompanySelectionTypes.SUBSIDIARY_MAPPING) {
            const subsidiaryColumnMappings = getSubsidiaryColumnMappings();
            if (subsidiaryColumnMappings?.mappings === undefined) {
              toast({
                title: 'Unable to load subsidiary mappings',
                status: 'error',
              });
              return CompanySelectionTypes.COMPANY_MAPPING;
            }
            onSubsidiaryEdit(subsidiaryColumnMappings.mappings);
            return CompanySelectionTypes.COMPANY_MAPPING;
          }

          if (
            resolvedState === null ||
            resolvedState === CompanySelectionTypes.SELECT_OPTION
          ) {
            onRequestClose();
            return previousState;
          }

          return resolvedState;
        });
      }}
      onClose={onRequestClose}
      returnToPrevious={onRequestClose}
      shouldAutoSubmit={shouldAutoSubmit}
      setShouldAutoSubmit={setShouldAutoSubmit}
      submitIsLoading={false}
      setSubmitIsLoading={() => undefined}
    />
  );
};
